import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { Dropdown, Popover, Whisper } from "rsuite";
import { Button, Col, Row } from "reactstrap";
import { findWhere } from "underscore";
import pluralize from "pluralize";

import SharedSelectedRecordsActions from "../../PageView/PageViewTable/SharedToolbar/SharedSelectedRecordsActions";
import MergeSubitemsDrawer from "../../Drawers/Subitems/MergeSubitemsDrawer/MergeSubitemsDrawer";
import useUpdateSubitemField from "../../../api/api-hooks/subitems/useUpdateSubitemField";
import useQueriedSubitems from "../../../api/api-hooks/records/useQueriedSubitems";
import useDeleteSubitems from "../../../api/api-hooks/records/useDeleteSubitems";
import BulkApplyTagsDrawer from "../../Drawers/Records/BulkApplyTagsDrawer";
import UpdateRecordsDrawer from "../../Drawers/Records/UpdateRecordsDrawer";
import { ReusableViewReferenceFields } from "../../../types/PageInterfaces";
import DropdownOptionWithIcon from "../../Menus/DropdownOptionWithIcon";
import DropdownMenuDivider from "../../Menus/DropdownMenuDivider";
import DataExportDrawer from "../../Drawers/DataExportDrawer";
import SubitemQuickCreateMenu from "./SubitemQuickCreateMenu";
import ImportCSVDrawer from "../../Drawers/ImportCSVDrawer";
import ReorderColumns from "../../AppNavbar/ReorderColumns";
import SubitemFiltersButton from "./SubitemFiltersButton";
import { Prefill } from "../../../types/GlobalInterfaces";
import TableActionsMenu from "../../CRM/TableActionsMenu";
import SortBy from "../../PageView/PageViewTable/SortBy";
import { Field } from "../../../types/TenantInterfaces";
import EditableTableView from "../../EditableTableView";
import SubitemTableLayout from "./SubitemTableLayout";
import useFieldMap from "../../../hooks/useFieldMap";
import useToggle from "../../../hooks/useToggle";
import DataSource from "../../Menus/DataSource";

interface Props {
  fieldSchema: Field;
  records: any[];
  parentRecord: any;
  showTitle: boolean;
  subitemAdding: boolean;
  subitemDuplicating?: boolean;
  module?: any;
  createMode?: boolean;
  prefills?: Prefill;
  publicForm?: boolean;
  summaryWidget?: boolean;
  setSubitems?: (subitems: any[]) => void;
  setAddingSubitem: (data: any) => void;
  toggleDuplicateSubitem: () => void;
  managePrefill: (prefill: any) => void;
  handleChange: (field: any, value: any, recordID: string) => void;
  reusableViewReferenceFields?: ReusableViewReferenceFields;
}

const emptyArray: [] = [];

interface SortFunc {
  Direction: "ASC" | "DESC";
  Field: string;
}

const TabbedMultiSubitem = ({
  fieldSchema,
  records,
  parentRecord,
  showTitle = true,
  module,
  createMode = false,
  publicForm = false,
  summaryWidget = false,
  setSubitems,
  setAddingSubitem,
  toggleDuplicateSubitem,
  managePrefill,
  handleChange,
  reusableViewReferenceFields,
}: Props) => {
  const [selectedLayout, setSelectedLayout] = useState(
    fieldSchema?.Query?.[0]?.Query?._SelectedLayout
  );
  const [mergeDrawer, toggleMergeDrawer] = useToggle();
  const [query, setQuery] = useState(fieldSchema?.Query || null);
  const providedLayouts = fieldSchema?.TableLayouts ?? [];
  // @ts-ignore
  const foundLayout = useMemo(() => {
    return findWhere(providedLayouts, {
      id: selectedLayout || "",
    });
  }, [selectedLayout]);

  const { updateSubitemField } = useUpdateSubitemField();
  const fieldMap: any = useFieldMap(
    parentRecord?._ModuleAPIName || module.APIName,
    undefined,
    false,
    fieldSchema?.APIName
  );
  const [selected, setSelected] = useState({});
  const [updateDrawer, toggleUpdateDrawer] = useToggle();
  const [tagDrawer, toggleTagDrawer] = useToggle();
  const [importCSVDrawer, toggleImportCSVDrawer] = useToggle();
  const { deleteSubitems } = useDeleteSubitems();
  const updateSortByRef = useRef<(sortBy: SortFunc | null) => void>();
  updateSortByRef.current = updateSortBy;
  const subQuery = query?.[0]?.Query;
  const [exportDrawer, setExportDrawer] = useState(false);

  const [selectedPrefill] = useState(null);
  const [editColumns, setEditColumns] = useState<Array<any>>([]);
  const [reorder, toggleReorder] = useToggle();
  const queryDisabled = !subQuery?.Filters?.length && !subQuery?.OrderBy;
  const [draggingID, setDraggingID] = useState("");

  //@ts-ignore
  const { record, recordLoading } = useQueriedSubitems({
    moduleAPIName: parentRecord?._ModuleAPIName,
    recordID: parentRecord?.id || parentRecord?._id,
    subQueries: query,
    disabled: queryDisabled,
  });

  const exportQuery = useMemo(() => {
    if (!parentRecord?.id || !fieldSchema?.APIName) return null;

    return {
      Fields: ["*"],
      Filters: [
        {
          Group: false,
          Field: "_id",
          Method: "equals",
          Value: parentRecord.id,
        },
      ],
      SubQueries: [
        {
          QueryField: fieldSchema?.APIName,
          Query: {
            ...query?.[0]?.Query,
            Fields: fieldSchema?.SubitemFields?.map((item) => item.APIName),
          },
        },
      ],
    };
  }, [fieldSchema, parentRecord?.id, query]);

  const finalRecords: any = useMemo(() => {
    if (!subQuery?.Filters?.length && !subQuery?.OrderBy) return records;
    if (recordLoading) return records;
    if (!record?.[fieldSchema?.APIName]) return [];

    return record?.[fieldSchema?.APIName];
  }, [subQuery, record, fieldSchema?.APIName, records, recordLoading]);

  const selectedSubitemsIDs = useMemo<Array<string>>(() => {
    const selectedKeys: string[] = Object.keys(selected);

    if (selectedKeys.length < 1) return emptyArray;

    const selectedSubitems = [];

    for (const key of selectedKeys) {
      //@ts-ignore
      selectedSubitems.push(finalRecords[key]?.id);
    }

    return selectedSubitems;
  }, [selected, finalRecords]);

  const selectedSubitems = useMemo(() => {
    const selectedKeys = Object.keys(selected);

    if (selectedKeys.length < 1) return emptyArray;

    const selectedSubitems = [];

    for (const key of selectedKeys) {
      //@ts-ignore
      selectedSubitems.push(finalRecords[key]);
    }

    return selectedSubitems;
  }, [selected, finalRecords]);

  const orderByField = query?.[0]?.Query?.OrderBy?.Field;
  const orderByDirection = query?.[0]?.Query?.OrderBy?.Direction;

  // @ts-ignore
  const custom = useMemo(() => {
    const orderBy = { Field: orderByField, Direction: orderByDirection };

    return fieldSchema?.SubitemFields?.map((field) => {
      const updatedAPIName = `${fieldSchema?.APIName}.${field.APIName}`;

      return {
        APIName: updatedAPIName,
        Name: field.Name,
        FieldType: field.FieldType,
        Protected: true,
        ReadOnly: true,
        order: orderBy ?? "",
        sortFunction: () => {
          if (orderByField === updatedAPIName && orderByDirection === "ASC") {
            if (updateSortByRef.current) return updateSortByRef.current(null);
            return;
          }
          if (updateSortByRef.current)
            updateSortByRef.current({
              Field: updatedAPIName,
              Direction: orderByField === updatedAPIName ? "ASC" : "DESC",
            });
        },
      };
    });
  }, [
    fieldSchema?.SubitemFields,
    fieldSchema?.APIName,
    orderByField,
    orderByDirection,
  ]);

  // Initialize / Sync table query
  useEffect(() => {
    if (!fieldSchema?.Query) {
      const newQuery = [
        {
          QueryField: fieldSchema?.APIName,
          Query: {
            ...query?.[0]?.Query,
            Fields: fieldSchema?.SubitemFields?.map((item) => item.APIName),
          },
        },
      ];

      updateSubitemField({
        moduleAPIName: parentRecord?._ModuleAPIName,
        fieldAPIName: fieldSchema?.APIName,
        payload: {
          Query: newQuery,
        },
      });
    }

    setQuery(fieldSchema?.Query);
  }, [fieldSchema?.APIName, fieldSchema?.Query]);

  const TableOptions = forwardRef((props, ref) => (
    <Dropdown.Menu
      /* @ts-ignore */
      ref={ref}
    >
      <h5 className="px-2 pt-2 text-dark">Manage</h5>
      <DropdownMenuDivider />
      <DropdownOptionWithIcon
        name="Reorder Columns"
        onClick={() => {
          toggleReorder();
          setEditColumns([...(query?.[0]?.Query?.Fields || [])]);
        }}
        icon="mdi mdi-reorder-vertical"
      />
    </Dropdown.Menu>
  ));

  const cancelReorder = () => {
    toggleReorder();
  };

  const saveReorder = () => {
    const newCols = editColumns.filter(
      (c) => c !== "id" && c !== "_RecordName"
    );
    const newQuery = [
      {
        QueryField: query?.[0]?.QueryField,
        Query: {
          ...query?.[0]?.Query,
          Fields: newCols,
        },
      },
    ];

    if (foundLayout) {
      const updatedLayout = foundLayout;
      updatedLayout.Fields = newCols;
      const updatedLayouts = providedLayouts.map((item) => {
        if (item.id === foundLayout.id) return updatedLayout;
        return item;
      });
      updateSubitemField({
        moduleAPIName: parentRecord?._ModuleAPIName,
        fieldAPIName: fieldSchema?.APIName,
        payload: {
          TableLayouts: updatedLayouts,
        },
        onSuccess: () => setQuery(newQuery),
      });
    } else {
      updateSubitemField({
        moduleAPIName: parentRecord?._ModuleAPIName,
        fieldAPIName: fieldSchema?.APIName,
        payload: {
          Query: newQuery,
        },
        onSuccess: () => setQuery(newQuery),
      });
    }

    toggleReorder();
  };

  const handleReorderColumns = (newCols: any) => {
    setEditColumns([...newCols]);
  };

  const handleDuplicateSubitems = () => {
    const subitem = selectedSubitems?.[0];
    const prefillPairs = Object.keys(subitem).reduce((arr, key) => {
      if (!subitem[key] && typeof subitem[key] !== "boolean") {
        return arr;
      }

      //@ts-ignore
      arr.push({ Field: key, Value: subitem[key] });
      return arr;
    }, []);

    //@ts-ignore
    managePrefill({ PrefillPairs: prefillPairs });

    toggleDuplicateSubitem();
  };

  const queryCreateMode = useMemo(() => {
    if (!createMode) return;
    const createQuery = fieldSchema?.Query?.[0]?.Query || {};

    createQuery.Fields =
      fieldSchema?.SubitemFields?.map((f) => f.APIName) || [];
    return createQuery;
  }, [fieldSchema?.Query]);

  if (!query) return null;

  return (
    <>
      {reorder ? (
        <>
          <Row>
            <Col>
              <h3>Reorder Table Columns</h3>
            </Col>
            <Col align="right">
              <Button
                size="sm"
                color="secondary"
                outline
                onClick={cancelReorder}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={saveReorder} color="primary">
                Save
              </Button>
            </Col>
          </Row>
          <ReorderColumns
            columns={editColumns.flatMap((column) => {
              if (column === "id" || column === "_RecordName") {
                return [];
              }

              if (!fieldMap[column]) return [];

              return [
                {
                  title: fieldMap[column].Name ?? column,
                  activeValue: column,
                },
              ];
            })}
            onDragEnd={handleReorderColumns}
          />
        </>
      ) : (
        <>
          {showTitle && <h3>{fieldSchema?.Name}</h3>}
          <div className="tw:flex tw:justify-between tw:items-center tw:mb-2">
            <div className="tw:flex flex-wrap" style={{ gap: "10px" }}>
              <SubitemQuickCreateMenu
                fieldSchema={fieldSchema}
                moduleAPIName={parentRecord?._ModuleAPIName || module.APIName}
                toggleAddSubitem={() => {
                  setAddingSubitem(true);
                }}
                toggleImportCSVDrawer={toggleImportCSVDrawer}
                parentRecord={parentRecord}
                setSelectedPrefill={(v: any) => {
                  managePrefill(v);
                }}
                selectedPrefill={selectedPrefill}
                publicForm={publicForm}
              />
              {!createMode && (
                <>
                  <SortBy
                    /* @ts-ignore */
                    custom={custom}
                    moduleAPIName={parentRecord?._ModuleAPIName}
                    sortBy={query?.[0]?.Query?.OrderBy}
                    updateSortBy={(sortBy: any) =>
                      updateSortBy(sortBy, "button")
                    }
                  />
                  <SubitemFiltersButton
                    module={module}
                    fieldSchema={fieldSchema}
                    query={query}
                    setQuery={setQuery}
                    parentRecord={parentRecord}
                  />
                  <SubitemTableLayout
                    module={module}
                    fieldSchema={fieldSchema}
                    query={query}
                    setQuery={setQuery}
                    parentRecord={parentRecord}
                    foundLayout={foundLayout}
                    selectedLayout={selectedLayout}
                    setSelectedLayout={setSelectedLayout}
                  />
                </>
              )}
            </div>
            {selectedSubitemsIDs?.length > 0 &&
              !createMode &&
              !publicForm &&
              !summaryWidget && (
                <SharedSelectedRecordsActions
                  selectedRecords={selectedSubitemsIDs}
                  onClear={() => setSelected({})}
                  setExportDrawer={setExportDrawer}
                  toggleUpdateRecordsModal={toggleUpdateDrawer}
                  toggleBulkApplyTags={toggleTagDrawer}
                  toggleMergeDrawer={toggleMergeDrawer}
                  deleteSelectedRecords={handleDeleteSubitems}
                  handleDuplicateRecord={handleDuplicateSubitems}
                  disabledSidebarCheck={true}
                />
              )}
            <div className="d-flex">
              {selectedSubitemsIDs?.length > 0 && summaryWidget && (
                <Whisper
                  placement="autoVerticalEnd"
                  trigger="click"
                  speaker={
                    <Popover className="sidebar-popover" full>
                      <TableActionsMenu
                        moduleAPIName={module.APIName}
                        selectedRecords={selectedSubitemsIDs}
                        setSelectedRecords={setSelected}
                        isSelection={selectedSubitemsIDs.length > 0}
                        setExportDrawer={setExportDrawer}
                        toggleUpdate={toggleUpdateDrawer}
                        toggleUpdateTags={toggleTagDrawer}
                        toggleMerge={toggleMergeDrawer}
                        deleteSelectedRecords={handleDeleteSubitems}
                        onDuplicate={handleDuplicateSubitems}
                        availableOptions={
                          createMode
                            ? [
                                "DeleteSelectedRecords",
                                "DuplicateRecords",
                                "MergeRecords",
                              ]
                            : [
                                "ExportSelectedRecords",
                                "BulkUpdateRecords",
                                "BulkTags",
                                "DeleteSelectedRecords",
                                "DuplicateRecords",
                                "MergeRecords",
                              ]
                        }
                        parentRecord={parentRecord}
                        publicForm={publicForm}
                      />
                    </Popover>
                  }
                >
                  <Button
                    size="sm"
                    color="primary"
                    className="btn-icon-only pl-2 pr-1 border-0"
                    style={{ width: "auto" }}
                  >
                    <span
                      style={{
                        position: "relative",
                        top: -2,
                        paddingLeft: 2,
                      }}
                    >
                      {selectedSubitemsIDs?.length}&nbsp;Selected
                    </span>
                    <i className="mdi mdi-chevron-down"></i>
                  </Button>
                </Whisper>
              )}
              {/* @ts-ignore */}
              <DataSource
                module={fieldSchema}
                disabled={createMode}
                isVisible={!selectedSubitemsIDs?.length}
                setExportDrawer={setExportDrawer}
                availableOptions={["ExportData"]}
              />
              <Whisper
                placement="autoVerticalEnd"
                trigger="click"
                speaker={
                  <Popover
                    className="sidebar-popover"
                    full
                    onClick={(e: any) => {
                      e.stopPropagation();
                    }}
                  >
                    <TableOptions />
                  </Popover>
                }
                controlId="newRecordDropdown"
              >
                <Button
                  size="sm"
                  color="secondary"
                  outline
                  disabled={createMode}
                  className="btn-icon-only"
                >
                  <i className="mdi mdi-dots-horizontal"></i>
                </Button>
              </Whisper>
            </div>
          </div>
          <EditableTableView
            containerClasses="pr-0 pt-2"
            query={createMode ? queryCreateMode : query?.[0]?.Query}
            setQuery={setQuery}
            records={finalRecords}
            fieldSchema={fieldSchema}
            parentRecord={parentRecord}
            /*@ts-ignore */
            selected={selected}
            /*@ts-ignore */
            setSelected={setSelected}
            emptyMessage={
              finalRecords === records
                ? null
                : "were found based on your search."
            }
            moduleAPIName={parentRecord?._ModuleAPIName || module.APIName}
            foundLayout={foundLayout}
            selectedLayout={selectedLayout}
            setSelectedLayout={setSelectedLayout}
            providedLayouts={providedLayouts}
            loading={createMode ? false : recordLoading && !queryDisabled}
            onSort={(sortBy: any) => updateSortBy(sortBy, "header")}
            createMode={createMode}
            columnAdd={!createMode}
            onChangeCreate={handleChange}
            setDraggingID={setDraggingID}
            draggingID={draggingID}
            reusableViewReferenceFields={reusableViewReferenceFields}
          />
          {finalRecords?.length > 0 && (
            <div className="mt-1">
              <small className="font-weight-500 text-dark">
                {finalRecords?.length}{" "}
                {pluralize(fieldSchema?.Name ?? "Record", finalRecords?.length)}
              </small>
            </div>
          )}
        </>
      )}
      <UpdateRecordsDrawer
        open={updateDrawer}
        onClose={toggleUpdateDrawer}
        records={selectedSubitemsIDs}
        moduleAPIName={parentRecord?._ModuleAPIName}
        parentRecordID={parentRecord?.id}
        /*@ts-ignore */
        multiSubitemAPIName={fieldSchema?.APIName}
      />
      <BulkApplyTagsDrawer
        open={tagDrawer}
        onClose={toggleTagDrawer}
        records={selectedSubitemsIDs}
        moduleAPIName={parentRecord?._ModuleAPIName}
        parentRecordID={parentRecord?.id}
        /*@ts-ignore */
        multiSubitemAPIName={fieldSchema?.APIName}
      />
      {exportDrawer && (
        <DataExportDrawer
          open={exportDrawer}
          moduleAPIName={module?.APIName}
          onClose={() => setExportDrawer(false)}
          query={exportQuery}
          recordIDs={selectedSubitemsIDs}
          multiSubitemField={fieldSchema?.APIName}
          parentRecordID={parentRecord.id}
        />
      )}
      <MergeSubitemsDrawer
        open={mergeDrawer}
        onClose={() => {
          toggleMergeDrawer();
          setSelected({});
        }}
        onMerge={() => setSelected({})}
        selectedSubitems={selectedSubitems}
        module={module}
        fieldSchema={fieldSchema}
        parentRecord={parentRecord}
        fieldMap={fieldMap}
        createMode={createMode}
        onSaveCreate={(payload: any) => {
          const newSubitem = records.filter(
            (rec) => rec.id !== payload.ItemToDelete
          )?.[0];
          const keys = Object.keys(newSubitem);
          keys.forEach((k) => {
            const subItemID = payload?.ItemPayload[k];
            if (subItemID)
              newSubitem[k] = records?.find((r) => r.id === subItemID)[k] || "";
          });
          const newSubitems = records.filter(
            (rec) => payload.ItemToDelete !== rec.id
          );
          if (typeof setSubitems === "function") setSubitems(newSubitems);
          setSelected([]);
          toggleMergeDrawer();
        }}
      />
      {/* @ts-ignore */}
      <ImportCSVDrawer
        open={importCSVDrawer}
        onClose={toggleImportCSVDrawer}
        onCreate={toggleImportCSVDrawer}
        moduleAPIName={parentRecord?._ModuleAPIName}
        /*@ts-ignore */
        fieldSchema={fieldSchema}
        isSubitem
        parentRecord={parentRecord}
      />
    </>
  );

  function handleDeleteSubitems() {
    if (createMode) {
      const newSubitems = records.filter((rec) => {
        if (!selectedSubitemsIDs.includes(rec.id || rec._id)) return rec;
      });
      if (typeof setSubitems === "function") setSubitems(newSubitems);
      setSelected([]);
      return;
    }
    deleteSubitems({
      selectedSubitems: selectedSubitemsIDs,
      fieldSchema,
      parentRecord,
      onSuccess: () => setSelected({}),
    });
  }

  function updateSortBy(
    sortBy: SortFunc | null,
    mode: "button" | "header" = "button"
  ) {
    const q = structuredClone(query);
    const newQuery = q[0]?.Query;

    newQuery.OrderBy = sortBy;

    if (newQuery.OrderBy === null) {
      delete q.OrderBy;
    } else {
      if (mode === "header") {
        newQuery.OrderBy.Field = `${fieldSchema.APIName}.${newQuery.OrderBy.Field}`;
      }
    }

    setQuery(q);
  }
};

export default TabbedMultiSubitem;
